import { useNavigate } from "react-router-dom";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { useGetProfileQuery } from "../../../store/api/authApi";
import {
	useGetConversationsQuery,
	type ConversationListItem,
} from "../../../store/api/conversationsApi";
import { formatDateTime } from "../../../utils/formatDate";

const CommunicationPanel = () => {
	const navigate = useNavigate();
	const { data: currentUser } = useGetProfileQuery();
	const { data: conversations = [], isLoading } = useGetConversationsQuery();

	// Get recent conversations (last 5)
	const recentConversations = conversations.slice(0, 5);

	const unreadCount = 0; // Conversations API doesn't provide unread count in list view

	return (
		<div className="bg-card border border-border rounded-lg">
			{/* Header */}
			<div className="p-4 border-b border-border">
				<div className="flex items-center justify-between">
					<div className="flex items-center space-x-2">
						<h3 className="font-semibold text-foreground">Messages</h3>
						{unreadCount > 0 && (
							<span className="bg-error text-error-foreground text-xs font-medium px-2 py-1 rounded-full">
								{unreadCount}
							</span>
						)}
					</div>
					<Button variant="ghost" size="sm" iconName="MoreHorizontal" />
				</div>
			</div>

			{/* Conversations List */}
			<div className="max-h-80 overflow-y-auto">
				{isLoading ? (
					<div className="p-6 text-center">
						<div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full mx-auto mb-3"></div>
						<p className="text-sm text-muted-foreground">Loading messages...</p>
					</div>
				) : recentConversations.length === 0 ? (
					<div className="p-6 text-center">
						<div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
							<Icon
								name="MessageSquare"
								size={24}
								className="text-muted-foreground"
							/>
						</div>
						<p className="text-sm text-muted-foreground">No messages</p>
						<p className="text-xs text-muted-foreground mt-1">
							You're all caught up!
						</p>
					</div>
				) : (
					<div className="divide-y divide-border">
						{recentConversations.map((conversation: ConversationListItem) => {
							return (
								<div
									key={conversation.id}
									className="p-4 cursor-pointer hover:bg-muted/50 transition-colors border-l-2 border-l-primary bg-primary/5"
									onClick={() => {
										navigate(`/messages?conversation=${conversation.id}`);
									}}
								>
									<div className="flex items-start space-x-3">
										<div className="w-8 h-8 rounded-full flex items-center justify-center bg-primary/10 text-primary">
											<Icon name="MessageSquare" size={16} />
										</div>
										<div className="flex-1 min-w-0">
											<div className="flex items-center justify-between mb-1">
												<p className="text-sm font-medium text-foreground truncate">
													{conversation.subject}
												</p>
												{conversation.last_message_at && (
													<span className="text-xs text-muted-foreground ml-2 flex-shrink-0">
														{formatDateTime(conversation.last_message_at)}
													</span>
												)}
											</div>
											<p className="text-xs text-muted-foreground">
												{conversation.participant_count}{" "}
												{conversation.participant_count === 1
													? "participant"
													: "participants"}
											</p>
										</div>
									</div>
								</div>
							);
						})}
					</div>
				)}
			</div>

			{/* Quick Actions */}
			<div className="p-4 border-t border-border">
				<Button
					variant="outline"
					size="sm"
					iconName="MessageSquare"
					iconPosition="left"
					onClick={() => navigate("/messages")}
					className="w-full"
				>
					View All
				</Button>
			</div>
		</div>
	);
};

export default CommunicationPanel;
